import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ShoppingBag, Users, AlertCircle, Package, RefreshCw } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError, devError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value, tint = "orange", testId }) {
  const tints = {
    orange: "bg-[#FFEDD5] text-[#E85D04]",
    green: "bg-[#2D6A4F]/10 text-[#2D6A4F]",
    blue: "bg-blue-100 text-blue-700",
    yellow: "bg-[#FFB703]/15 text-[#B5790F]",
  };
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl grid place-items-center ${tints[tint]}`}>
        <Icon size={20} />
      </div>
      <div className="text-xs uppercase tracking-widest text-[#5C5C5C] font-bold mt-3">{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

const EMPTY = {
  today_revenue: 0,
  month_revenue: 0,
  today_orders: 0,
  total_orders: 0,
  total_customers: 0,
  pending_orders: 0,
  paid_to_process: 0,
  bestsellers: [],
  recent_orders: [],
  daily_revenue: [],
};

export default function AdminDashboard() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/dashboard");
      // Defensive merge: jangan crash kalau backend lupa kirim field
      setData({ ...EMPTY, ...(r.data || {}) });
    } catch (e) {
      devError("Dashboard load failed:", e?.message || e);
      setError(formatApiError(e));
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Dashboard</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Pantau bisnis Anda secara real-time</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="rounded-xl" data-testid="dashboard-refresh-btn" disabled={loading}>
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> {loading ? "Memuat..." : "Refresh"}
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="dashboard-error">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">Tidak bisa memuat data dashboard</div>
            <div className="text-xs mt-0.5 opacity-80">{error}. Menampilkan data kosong sebagai pengganti.</div>
          </div>
          <Button size="sm" variant="outline" onClick={load} className="rounded-lg h-7 px-2 text-xs">Coba lagi</Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat icon={TrendingUp} label="Omzet Hari Ini" value={formatRp(data.today_revenue)} testId="stat-today-revenue" />
        <Stat icon={TrendingUp} label="Omzet Bulan Ini" value={formatRp(data.month_revenue)} tint="green" testId="stat-month-revenue" />
        <Stat icon={ShoppingBag} label="Pesanan Hari Ini" value={data.today_orders} tint="blue" testId="stat-today-orders" />
        <Stat icon={Users} label="Total Pelanggan" value={data.total_customers} tint="yellow" testId="stat-total-customers" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4">
        <Stat icon={Package} label="Total Pesanan" value={data.total_orders} testId="stat-total-orders" />
        <Stat icon={AlertCircle} label="Menunggu Bayar" value={data.pending_orders} tint="yellow" testId="stat-pending" />
        <Stat icon={AlertCircle} label="Bayar Terkonfirmasi" value={data.paid_to_process} tint="blue" testId="stat-paid" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <section className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-2xl p-5">
          <h3 className="font-display font-bold mb-3">Omzet 7 Hari Terakhir</h3>
          <div className="h-64">
            {data.daily_revenue && data.daily_revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.daily_revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="date" stroke="#5C5C5C" fontSize={11} />
                  <YAxis stroke="#5C5C5C" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => formatRp(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#E85D04" strokeWidth={3} dot={{ fill: "#E85D04", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-[#5C5C5C]">Belum ada data omzet</div>
            )}
          </div>
        </section>

        <section className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
          <h3 className="font-display font-bold mb-3">Produk Terlaris</h3>
          <div className="space-y-3">
            {(data.bestsellers || []).map((p, idx) => (
              <div key={p.id || idx} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#FFEDD5] text-[#E85D04] grid place-items-center text-xs font-black">{idx + 1}</div>
                <div className="w-10 h-10 rounded-lg bg-[#FAF9F6] overflow-hidden shrink-0">
                  {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold line-clamp-1">{p.name}</div>
                  <div className="text-[10px] text-[#5C5C5C]">{p.sold_count || 0} terjual</div>
                </div>
              </div>
            ))}
            {(!data.bestsellers || data.bestsellers.length === 0) && (
              <div className="text-xs text-[#5C5C5C]">Belum ada penjualan</div>
            )}
          </div>
        </section>
      </div>

      <section className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">Pesanan Terbaru</h3>
          <Link to="/admin/pesanan" className="text-xs text-[#E85D04] font-bold">Lihat semua →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="py-2">No Pesanan</th>
                <th className="py-2">Pelanggan</th>
                <th className="py-2">Total</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_orders || []).map((o) => (
                <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]">
                  <td className="py-3"><Link to={`/admin/pesanan/${o.id}`} className="font-mono text-xs text-[#E85D04] font-bold">{o.order_number}</Link></td>
                  <td className="py-3">{o.user_name || o.user_email}</td>
                  <td className="py-3 font-bold">{formatRp(o.total)}</td>
                  <td className="py-3"><span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-[#FAF9F6]">{o.status}</span></td>
                </tr>
              ))}
              {(!data.recent_orders || data.recent_orders.length === 0) && (
                <tr><td colSpan={4} className="py-8 text-center text-xs text-[#5C5C5C]">Belum ada pesanan</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}
